<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { t } from '../i18n';
import { formatMoney } from '../format';

// Portfolio dropdown (docs/portfolios/SPEC.md, decision 16) behind a centred pill on desktop. The closed button shows no sum: the card
// and the analytics show the live one, a snapshot here would be a third number (docs/design/SPEC.md, item 2).
const props = defineProps({
  views: {
    type: Array,
    required: true,
  },
  selectedKey: {
    type: String,
    required: true,
  },
  // Live price dot before the name (docs/design/SPEC.md, decision 30): 'on' | 'off' | null, and its text
  // for the tooltip and screen readers. Only an indicator: a click opens the list as anywhere on the button.
  liveState: {
    type: String,
    default: null,
  },
  liveLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['select', 'create-group']);

const open = ref(false);
const root = ref(null);

const current = computed(() => props.views.find((v) => v.key === props.selectedKey) ?? props.views[0]);

const sections = computed(() => [
  { key: 'capital', title: null, items: props.views.filter((v) => v.kind === 'capital') },
  { key: 'accounts', title: t('portfolios.accountsSection'), items: props.views.filter((v) => v.kind === 'account') },
  { key: 'groups', title: t('portfolios.groupsSection'), items: props.views.filter((v) => v.kind === 'group') },
].filter((s) => s.items.length > 0));

function label(view) {
  return view.name || view.account_id;
}

function choose(view) {
  open.value = false;
  emit('select', view.key);
}

function createGroup() {
  open.value = false;
  emit('create-group');
}

function onDocumentClick(event) {
  if (open.value && root.value && !root.value.contains(event.target)) {
    open.value = false;
  }
}

function onKeydown(event) {
  if (event.key === 'Escape') {
    open.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <!-- A centred pill as in the phone header (docs/design/SPEC.md, decision 31); the list drops below it. -->
  <div
    ref="root"
    class="relative mx-auto w-fit max-w-full"
  >
    <button
      type="button"
      class="flex h-8 max-w-full items-center gap-1 rounded-full bg-slate-900 px-3.5 text-[0.9375rem] font-medium text-slate-100 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
      :aria-label="liveState ? `${t('portfolios.select')}. ${liveLabel}` : t('portfolios.select')"
      :title="liveState ? liveLabel : undefined"
      aria-haspopup="listbox"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span
        v-if="liveState"
        class="mr-1 size-2.5 shrink-0 rounded-full"
        :class="liveState === 'on' ? 'bg-emerald-400' : 'border-2 border-amber-400'"
        aria-hidden="true"
      />
      <span class="truncate">{{ current ? label(current) : '' }}</span>
      <svg
        class="size-4 shrink-0 text-slate-400 transition"
        :class="open ? 'rotate-180' : ''"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>

    <div
      v-if="open"
      class="absolute left-1/2 top-full z-50 mt-2 max-h-[70vh] w-80 -translate-x-1/2 overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 py-1 shadow-xl"
      role="listbox"
    >
      <div
        v-for="section in sections"
        :key="section.key"
        class="border-b border-slate-800 py-1 last:border-b-0"
      >
        <p
          v-if="section.title"
          class="px-4 pb-1 pt-1.5 text-[0.6875rem] font-medium uppercase tracking-wide text-slate-500"
        >
          {{ section.title }}
        </p>
        <button
          v-for="view in section.items"
          :key="view.key"
          type="button"
          role="option"
          :aria-selected="view.key === current?.key"
          class="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-slate-800"
          :class="view.key === current?.key ? 'text-emerald-400' : 'text-slate-200'"
          @click="choose(view)"
        >
          <span class="min-w-0 flex-1 truncate">{{ label(view) }}</span>
          <span class="shrink-0 tabular-nums text-slate-400">{{ formatMoney(view.total_amount, view.currency) }}</span>
        </button>
      </div>
      <button
        type="button"
        class="w-full border-t border-slate-800 px-4 py-2 text-left text-sm text-emerald-400 hover:bg-slate-800"
        @click="createGroup"
      >
        {{ t('portfolios.newGroup') }}
      </button>
    </div>
  </div>
</template>
